import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import { ArrowRight, Loader2 } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";

const SERVICES = [
  { value: "parole", label: "Parole Packet" },
  { value: "grievance", label: "Auto Grievance" },
  { value: "divorce", label: "Divorce (NY)" },
  { value: "immigration", label: "Immigration Assistance" },
  { value: "hearing", label: "Hearing Preparation" },
  { value: "petition", label: "Petition Preparation" },
];

export default function Intake() {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    full_name: "", email: "", phone: "",
    incarcerated: false, din: "", facility: "",
    service: "", urgency: "medium", description: "",
  });
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.service) { toast.error("Please select a service to continue."); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post("/intake/general", form);
      toast.success("Intake received — routing you to the next step.");
      navigate(`${data.next_route}?from=${data.id}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setSubmitting(false); }
  };

  return (
    <div className="bg-black text-zinc-100 min-h-screen pt-12 pb-24">
      <div className="container-pl max-w-2xl">
        <p className="overline mb-4">Step 1 of 2 · Quick intake</p>
        <h1 className="display-serif text-4xl sm:text-5xl text-zinc-50 leading-tight mb-3 accent-rule">
          Tell us who you are.
        </h1>
        <p className="text-base text-zinc-400 mb-10 max-w-xl leading-relaxed">
          A short form to route you to the correct intake. Takes under 60 seconds. All information is confidential.
        </p>

        <form onSubmit={submit} className="space-y-6" data-testid="general-intake-form">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="full_name" className="text-zinc-300">Full Name</Label>
              <Input id="full_name" required value={form.full_name} onChange={(e) => update("full_name", e.target.value)} className="bg-zinc-950 border-zinc-800 mt-1.5" data-testid="intake-name"/>
            </div>
            <div>
              <Label htmlFor="email" className="text-zinc-300">Email</Label>
              <Input id="email" type="email" required value={form.email} onChange={(e) => update("email", e.target.value)} className="bg-zinc-950 border-zinc-800 mt-1.5" data-testid="intake-email"/>
            </div>
            <div className="sm:col-span-2">
              <Label htmlFor="phone" className="text-zinc-300">Phone</Label>
              <Input id="phone" required value={form.phone} onChange={(e) => update("phone", e.target.value)} className="bg-zinc-950 border-zinc-800 mt-1.5" data-testid="intake-phone"/>
            </div>
          </div>

          <div className="flex items-center gap-3 border border-zinc-800 rounded-sm px-4 py-3 bg-zinc-950">
            <Checkbox id="incarc" checked={form.incarcerated} onCheckedChange={(v) => update("incarcerated", !!v)} className="border-amber-400/60 data-[state=checked]:bg-amber-400 data-[state=checked]:text-black" data-testid="intake-incarcerated"/>
            <Label htmlFor="incarc" className="text-sm text-zinc-200 cursor-pointer">Currently incarcerated</Label>
          </div>

          {form.incarcerated && (
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="din" className="text-zinc-300">DIN</Label>
                <Input id="din" value={form.din} onChange={(e) => update("din", e.target.value)} className="bg-zinc-950 border-zinc-800 mt-1.5" data-testid="intake-din"/>
              </div>
              <div>
                <Label htmlFor="facility" className="text-zinc-300">Facility</Label>
                <Input id="facility" value={form.facility} onChange={(e) => update("facility", e.target.value)} className="bg-zinc-950 border-zinc-800 mt-1.5" data-testid="intake-facility"/>
              </div>
            </div>
          )}

          <div>
            <Label className="text-zinc-300">Service Needed</Label>
            <Select value={form.service} onValueChange={(v) => update("service", v)} required>
              <SelectTrigger className="bg-zinc-950 border-zinc-800 mt-1.5 text-zinc-100" data-testid="intake-service">
                <SelectValue placeholder="Choose a service" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
                {SERVICES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-zinc-300 mb-2 block">Urgency</Label>
            <RadioGroup value={form.urgency} onValueChange={(v) => update("urgency", v)} className="flex gap-6">
              {["low", "medium", "high"].map((u) => (
                <div key={u} className="flex items-center gap-2">
                  <RadioGroupItem value={u} id={`u-${u}`} className="border-amber-400/60 text-amber-400" />
                  <Label htmlFor={`u-${u}`} className="capitalize text-sm cursor-pointer">{u}</Label>
                </div>
              ))}
            </RadioGroup>
          </div>

          <div>
            <Label htmlFor="desc" className="text-zinc-300">Brief Description</Label>
            <Textarea id="desc" rows={4} value={form.description} onChange={(e) => update("description", e.target.value)} className="bg-zinc-950 border-zinc-800 mt-1.5" data-testid="intake-description" placeholder="A few sentences about your situation so we can prepare." />
          </div>

          <p className="text-[11px] text-zinc-500 italic">
            We are not attorneys and do not provide legal advice. Submitting this form does not create an attorney–client relationship.
          </p>

          <div className="flex items-center justify-between pt-2">
            <Link to="/services" className="text-sm text-zinc-400 hover:text-amber-400 transition-colors">← Back to services</Link>
            <Button type="submit" size="lg" disabled={submitting} className="rounded-sm bg-blue-700 hover:bg-blue-600 text-white" data-testid="intake-submit">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Continue <ArrowRight className="ml-2 h-4 w-4" strokeWidth={1.5} /></>}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
